/**
 * Deal Alerts API Service (LPM-1 read surface)
 *
 * Type-safe client for participation-marketplace deal alerts: broadcast
 * fi_treasury Notification rows written by the buy-box match fan-out. The
 * backend lives in platform-admin-api at /api/v1/deal-alerts, gated on
 * pledge:read:inbox (the fi_officer/fi_ops reading fallback).
 *
 * Types derived from platform-admin.openapi.yaml -- no manual interfaces.
 *
 * Note on baseUrl: the canonical spec authors this path in full
 * (`/api/v1/deal-alerts`, per api-version-canonical.md), so the client
 * anchors at the origin ('') to keep the generated path key intact while
 * producing the correct runtime URL.
 */

import createClient from 'openapi-fetch';
import type { paths as PlatformAdminPaths } from '@aaimplatform/institutional-types/platform-admin';
import type { components } from '@aaimplatform/institutional-types/platform-admin';
import { authMiddleware } from '../client';

export type DealAlert = components['schemas']['DealAlert'];
export type DealAlertListResponse = components['schemas']['DealAlertListResponse'];

const dealAlertsClient = createClient<PlatformAdminPaths>({ baseUrl: '', credentials: 'include' });
dealAlertsClient.use(authMiddleware);

function unwrap<T>(result: { data?: T; error?: unknown; response: Response }): T {
  if (result.error || result.data === undefined) {
    const status = result.response?.status ?? 0;
    const statusText = result.response?.statusText ?? 'Unknown';
    const body = result.error as { message?: string } | undefined;
    throw new Error(body?.message ?? `HTTP ${status}: ${statusText}`);
  }
  return result.data;
}

export const dealAlertsApi = {
  list: async (limit?: number): Promise<DealAlertListResponse> => {
    const result = await dealAlertsClient.GET('/api/v1/deal-alerts', {
      params: { query: { limit } },
    });
    return unwrap(result);
  },
};
